<template>
  <base-view id="login-portal">
    <div class="login-portal">
      <!-- Header -->
      <div class="header-block">
        <div class="max-w-2xl mx-auto px-6 pt-14 pb-4 text-center">
          <h1
            class="m-0 text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight"
          >
            Login &amp; Register
          </h1>
          <p class="m-0 mt-3 text-gray-500">
            Select who you are so we can get you to the right place.
          </p>
        </div>

        <!-- Audience picker -->
        <div class="max-w-2xl mx-auto px-6 pb-12">
          <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-5"
            role="group"
            aria-label="Choose your account type"
          >
            <button
              type="button"
              @click="selectAudience('participant')"
              :aria-pressed="audience === 'participant'"
              class="audience-card"
              :class="
                audience === 'participant' ? 'audience-card-selected' : ''
              "
            >
              <span class="audience-icon">
                <span class="flaticon-lineal" aria-hidden="true"></span>
              </span>
              <span class="audience-title">Participants / Employees</span>
              <span class="audience-sub"
                >For those who have a benefits plan.</span
              >
            </button>

            <button
              type="button"
              @click="selectAudience('employer')"
              :aria-pressed="audience === 'employer'"
              class="audience-card"
              :class="audience === 'employer' ? 'audience-card-selected' : ''"
            >
              <span class="audience-icon">
                <span class="flaticon-management" aria-hidden="true"></span>
              </span>
              <span class="audience-title">Employers</span>
              <span class="audience-sub">For client HR departments.</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Destination cards -->
      <div class="max-w-4xl mx-auto px-6 py-10 pb-16">
        <!-- Placeholder when no audience selected -->
        <template v-if="audience === null">
          <div class="no-audience-state">
            <img src="@/assets/RMRGreen_Logo.png" alt="Rocky Mountain Reserve" class="no-audience-logo" />
            <p class="no-audience-tagline">Benefits administration made simple.</p>
            <div class="no-audience-stats">
              <div class="no-audience-stat">
                <span class="no-audience-stat-value">93%</span>
                <span class="no-audience-stat-label">Client satisfaction rate</span>
              </div>
              <div class="no-audience-stat-divider" aria-hidden="true"></div>
              <div class="no-audience-stat">
                <span class="no-audience-stat-value">71+</span>
                <span class="no-audience-stat-label">Years combined experience</span>
              </div>
              <div class="no-audience-stat-divider" aria-hidden="true"></div>
              <div class="no-audience-stat">
                <span class="no-audience-stat-value">207k</span>
                <span class="no-audience-stat-label">Plans administered</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Employee destinations -->
        <template v-else-if="audience === 'participant'">
          <h2 class="section-title" ref="destinationHeading" tabindex="-1">
            What kind of account do you have?
          </h2>
          <p class="section-sub">
            Not sure? Health Benefits Login is the right place for most people.
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
            <div class="dest-card dest-card-primary">
              <div class="dest-title dest-title-light">
                Health Benefits Login
              </div>
              <p class="dest-desc dest-desc-light">
                FSA, DCA, HRA, HSA, transit/parking, and other benefit accounts.
              </p>
              <a
                href="https://user.rmrbenefits.com/login"
                target="_blank"
                rel="noopener noreferrer"
                class="dest-btn dest-btn-white mt-auto"
                >Employee Login &rarr;<span class="sr-only">
                  (opens in new tab)</span
                ></a
              >
            </div>

            <div class="dest-card dest-card-secondary">
              <div class="dest-title">COBRA Login</div>
              <p class="dest-desc">
                Continuing insurance coverage from a previous employer.
              </p>
              <div class="flex flex-wrap gap-2 mt-auto">
                <a
                  href="https://cobrapoint.benaissance.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="dest-btn dest-btn-green"
                  >COBRA Login<span class="sr-only">
                    (opens in new tab)</span
                  ></a
                >
                <a
                  href="https://cobrapoint.benaissance.com/Registration/Identify"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="dest-btn dest-btn-muted"
                  >Register<span class="sr-only"> (opens in new tab)</span></a
                >
              </div>
            </div>
          </div>
        </template>

        <!-- Employer destinations -->
        <template v-else-if="audience === 'employer'">
          <h2 class="section-title" ref="destinationHeading" tabindex="-1">
            What kind of account are you looking for?
          </h2>

          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6"
          >
            <div class="dest-card dest-card-primary">
              <div class="dest-title dest-title-light">Benefits Login</div>
              <p class="dest-desc dest-desc-light">
                Manage your company's FSA, DCA, HRA, HSA, PKG/TRN, or other
                benefit accounts.
              </p>
              <a
                href="https://admin.rmrbenefits.com/login"
                target="_blank"
                rel="noopener noreferrer"
                class="dest-btn dest-btn-white mt-auto"
                >Employer Login &rarr;<span class="sr-only">
                  (opens in new tab)</span
                ></a
              >
            </div>

            <div class="dest-card dest-card-secondary">
              <div class="dest-title">COBRA Login</div>
              <p class="dest-desc">
                For former employees continuing insurance/health benefits after
                termination.
              </p>
              <div class="flex flex-wrap gap-2 mt-auto">
                <a
                  href="https://cobrapoint.benaissance.com/employerportal/login?state=eyJjYW5jZWxfdXJpIjoiaHR0cHM6Ly9jb2JyYXBvaW50LmJlbmFpc3NhbmNlLmNvbS8iLCJsb2dvdXRfdXJpIjoiaHR0cHM6Ly9jb2JyYXBvaW50LmJlbmFpc3NhbmNlLmNvbS8ifQ%3d%3d"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="dest-btn dest-btn-green"
                  >COBRA Login<span class="sr-only">
                    (opens in new tab)</span
                  ></a
                >
                <a
                  href="https://cobrapoint.benaissance.com/Registration/Identify"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="dest-btn dest-btn-muted"
                  >Register<span class="sr-only"> (opens in new tab)</span></a
                >
              </div>
            </div>

            <div class="dest-card dest-card-primary">
              <div class="dest-title dest-title-light">FTP Login</div>
              <p class="dest-desc dest-desc-light">
                Upload your data safely and securely.
              </p>
              <a
                href="https://rmr.files.com/login"
                target="_blank"
                rel="noopener noreferrer"
                class="dest-btn dest-btn-white mt-auto"
                >FTP Login &rarr;<span class="sr-only">
                  (opens in new tab)</span
                ></a
              >
            </div>
          </div>
        </template>
      </div>
    </div>
  </base-view>
</template>

<script>
import BaseView from "@/components/layout/BaseView.vue";

export default {
  name: "LoginPortal",
  components: { BaseView },
  data() {
    return { audience: null };
  },
  created() {
    const a = this.$route.query.audience;
    if (a === "participant" || a === "employer") {
      this.audience = a;
    }
  },
  methods: {
    selectAudience(value) {
      this.audience = value;
      this.$router.replace({ query: { audience: value } }).catch(() => {});
      this.$nextTick(() => {
        if (this.$refs.destinationHeading) {
          this.$refs.destinationHeading.focus();
        }
      });
    }
  }
};
</script>

<style scoped>
.login-portal {
  min-height: calc(100vh - 10rem);
  background: #fafafa;
}
.header-block {
  background: #ffffff;
  border-bottom: 1px solid #ededed;
}

/* ── No-audience placeholder ── */
.no-audience-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 1rem 3rem;
  gap: 1rem;
}
.no-audience-logo {
  height: 3.5rem;
  width: auto;
  opacity: 0.9;
}
.no-audience-tagline {
  margin: 0;
  font-size: 1.0625rem;
  color: #6b7280;
}
.no-audience-stats {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
}
.no-audience-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}
.no-audience-stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #096843;
  line-height: 1;
}
.no-audience-stat-label {
  font-size: 0.8125rem;
  color: #6b7280;
  text-align: center;
}
.no-audience-stat-divider {
  width: 1px;
  height: 2.5rem;
  background: #e5e7eb;
}
@media (max-width: 480px) {
  .no-audience-stat-divider { display: none; }
}

/* ── Section headings ── */
.section-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 800;
  color: #111827;
  outline: none;
}
.section-title:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 4px;
  border-radius: 0.25rem;
}
.section-sub {
  margin: 0.375rem 0 0;
  font-size: 1rem;
  color: #6b7280;
}

/* ── Audience picker cards ── */
.audience-card {
  background: #ffffff;
  border: 2px solid #e5e5e5;
  border-radius: 0.75rem;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.audience-card:hover {
  border-color: #9fe8b9;
}
.audience-card:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 3px;
}
.audience-card-selected {
  background: #f6fdf9;
  border-color: #096843;
  box-shadow: 0 0 0 3px rgba(9, 104, 67, 0.12);
}

.audience-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 0.75rem;
  background: #defae6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}
.audience-icon .flaticon-lineal,
.audience-icon .flaticon-management {
  font-size: 1.5rem;
  color: #096843;
}
.audience-title {
  font-weight: 700;
  font-size: 1.125rem;
  color: #111827;
  margin-bottom: 0.375rem;
}
.audience-sub {
  font-size: 1rem;
  color: #6b7280;
}

/* ── Destination cards ── */
.dest-card {
  border-radius: 0.9rem;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Primary: solid RMR green */
.dest-card-primary {
  background: #096843;
  box-shadow: 0 4px 16px -4px rgba(9, 104, 67, 0.35);
}

/* Secondary: white with border */
.dest-card-secondary {
  background: #ffffff;
  border: 1.5px solid #e5e5e5;
}

.dest-title {
  font-weight: 800;
  font-size: 1.125rem;
  color: #111827;
}
.dest-title-light {
  color: #ffffff;
}
.dest-desc {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: #6b7280;
  flex-grow: 1;
}
.dest-desc-light {
  color: rgba(255, 255, 255, 0.85);
}

/* Destination buttons */
.dest-btn {
  display: inline-block;
  width: fit-content;
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 0.9375rem;
  text-decoration: none;
  white-space: nowrap;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.15s ease;
}
.dest-btn:hover {
  opacity: 0.85;
}
.dest-btn:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 3px;
}
.dest-btn-white {
  background: #ffffff;
  color: #096843;
}
.dest-btn-green {
  background: #096843;
  color: #ffffff;
}
.dest-btn-muted {
  background: #f2f2f2;
  color: #4b4b4b;
}
</style>
